import createClient from 'openapi-fetch';
import { useAuth } from '@/stores/auth';
import { router } from '@/router';
/**
 * Single seam between Vue components and the HoaWatch.Api OpenAPI surface.
 *
 * NEVER bypass this wrapper. NEVER add fetch-by-hand in components. NEVER add
 * third-party API SDKs (OpenAI, Twilio, SendGrid, Facebook) — all external
 * service calls route through HoaWatch.Api per design spec §3.1.
 */
const authMiddleware = {
    async onRequest({ request }) {
        // Skip auth header for public endpoints.
        // Portal endpoints are anonymous by design (D-P per spec §7.4 — no client-side
        // third-party calls; Turnstile token is verified server-side, not here).
        if (request.url.includes('/v1/public/'))
            return request;
        if (request.url.includes('/v1/portal/'))
            return request;
        const auth = useAuth();
        const token = await auth.getAccessToken();
        if (token)
            request.headers.set('Authorization', `Bearer ${token}`);
        return request;
    },
    async onResponse({ response }) {
        if (response.status === 401) {
            // Token rejected — bounce to login. The auth store is cleared on the
            // login page load (MSAL's handleRedirectPromise handles cleanup).
            const target = router.currentRoute.value.fullPath;
            await router.push({ path: '/login', query: { return: target } });
        }
        return response;
    },
};
// `''` baseUrl uses the dev proxy in dev (Vite forwards /v1/* to the API).
// In prod, VITE_API_PROD_URL overrides to https://api.hoawatch.us.
// NOTE: Do NOT use VITE_API_BASE_URL here — that variable is the proxy TARGET
// in vite.config.ts (backend URL); it must NOT be the client's base URL
// (which would bypass the proxy).
const baseUrl = import.meta.env.VITE_API_PROD_URL ?? '';
export const api = createClient({ baseUrl });
api.use(authMiddleware);
/**
 * Typed helper for the dashboard's first call. Slice plans add more helpers
 * here; views import from `@/api/client`, never from `./generated` directly.
 *
 * @deprecated Use useIssues() composable instead (closes risk D.2).
 */
export async function listIssues() {
    const { data, error } = await api.GET('/v1/issues', {
        params: { query: { limit: 10 } },
    });
    if (error)
        throw new Error(`Failed to load issues: ${JSON.stringify(error)}`);
    return data?.items ?? [];
}
